import type { components } from "@/lib/api.gen";

type Notification = components["schemas"]["Notification"];

/**
 * Minimal shape of a next-intl translator, so this module doesn't depend on
 * next-intl's generics. Matches how `t` is called elsewhere: `t(key, values?)`.
 */
type Translator = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export interface RenderedNotification {
  title: string;
  body: string;
}

/**
 * Render a data-driven notification into localized title/body. The backend
 * stores only a `type` + structured `data`; this is the in-app render surface
 * (the email path renders its own German text server-side).
 *
 * Messages live under `notifications.types.<type>.{title,body}` in the locale
 * files, with ICU placeholders filled from the payload.
 */
export function renderNotification(
  t: Translator,
  n: Notification,
): RenderedNotification {
  const rendered = renderNotificationBase(t, n);
  // When a notification was redirected from a managed persona to its super-user,
  // attribute it so an owner of several "Helden" can tell them apart.
  if (n.on_behalf_of) {
    return {
      title: rendered.title,
      body: t("notifications.onBehalfOf", {
        name: n.on_behalf_of,
        body: rendered.body,
      }),
    };
  }
  return rendered;
}

function renderNotificationBase(
  t: Translator,
  n: Notification,
): RenderedNotification {
  const d = n.data;
  // The notifications.types.* / notifications.statusLabel.* keys used below are
  // validated by scripts/check-translations.ts — update NOTIFICATION_TYPES there
  // when adding a notification type or changing which title/body suffixes it reads.
  const key = (suffix: string) => `notifications.types.${d.type}.${suffix}`;

  switch (d.type) {
    case "admission_approved":
    case "admission_rejected":
    case "event_capacity_reached":
    case "event_admin_granted":
      return {
        title: t(key("title")),
        body: t(key("body"), { eventTitle: d.event_title }),
      };
    case "admission_requested":
      return {
        title: t(key("title")),
        body: t(key("body"), {
          name: d.requester_name,
          eventTitle: d.event_title,
        }),
      };
    case "org_event_created":
      return {
        title: t(key("title")),
        body: t(key("body"), {
          name: d.creator_name,
          eventTitle: d.event_title,
          orgName: d.org_name,
        }),
      };
    case "org_slot_bill":
      return {
        title: t(key("title")),
        body: t(key("body"), {
          orgName: d.org_name,
          slots: d.slot_count,
          amount: (d.amount_cents / 100).toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          }),
          periodStart: new Date(d.period_start).toLocaleDateString("de-DE"),
          periodEnd: new Date(d.period_end).toLocaleDateString("de-DE"),
          dueDate: new Date(d.due_at).toLocaleDateString("de-DE"),
        }),
      };
    case "event_status_changed":
      return {
        title: t(key("title")),
        body: t(key("body"), {
          eventTitle: d.event_title,
          status: t(`notifications.statusLabel.${d.new_status}`),
        }),
      };
    case "event_details_changed":
      return {
        title: t(key("title")),
        body: t(key(d.date_changed ? "bodyDate" : "body"), {
          eventTitle: d.event_title,
        }),
      };
    case "job_instance_full":
    case "job_instance_changed":
    case "job_instance_cancelled":
      return {
        title: t(key("title")),
        body: t(key("body"), { jobTitle: d.job_title }),
      };
    case "job_assigned":
    case "job_unassigned":
      return {
        title: t(key("title")),
        body: t(key("body"), {
          jobTitle: d.job_title,
          eventTitle: d.event_title,
        }),
      };
    case "org_member_added":
      return {
        title: t(key("title")),
        body: t(key(d.admin ? "bodyAdmin" : "body"), { orgName: d.org_name }),
      };
    // Free-text notifications: the payload *is* the text.
    case "admin_message":
    case "test":
      return { title: d.title, body: d.body };
    default: {
      // Exhaustiveness guard: a new payload type must be handled above.
      const _exhaustive: never = d;
      return _exhaustive;
    }
  }
}
